import { memo } from "react";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { CliIcon, RulesIcon } from "@icons";
import { ActorKind, AuditLogChangeType } from "@common/types/auditLog";
import { DEFAULT_ICON_COLOR, resolveActorKind } from "../utils/actorUtils";
import styles from "./ActorIcon.module.scss";
import { integrationActors } from "./integrationActors";

interface UserIconProps {
    preferredName?: string | null;
    avatar?: string | null;
    iconColor?: string | null;
    email?: string | null;
}

interface ActorIconProps {
    actorType: string;
    actorName: string | null;
    sourceType: string;
    user?: UserIconProps;
    fields?: string[];
    changeTypes?: AuditLogChangeType[];
    system?: string | null;
    /** From `useIsVisibleAgentActor`: shows the Kick avatar for the Kick agent. */
    isAgentActor?: boolean;
    /** @default "xs" */
    size?: "xs" | "sm";
}

export const ActorIcon = memo(
    ({
        actorType,
        actorName,
        sourceType,
        user,
        fields,
        changeTypes,
        system,
        isAgentActor,
        size = "xs",
    }: ActorIconProps) => {
        const kind = resolveActorKind({
            actorType,
            sourceType,
            fields,
            changeTypes,
            system,
            isAgentActor,
        });

        switch (kind) {
            case ActorKind.INTEGRATION: {
                const integration = integrationActors[sourceType];
                if (!integration) {
                    return null;
                }
                return (
                    <InstitutionIcon
                        institution={integration.institution}
                        size={size}
                        label={integration.label}
                    />
                );
            }
            case ActorKind.USER:
                return (
                    <UserAvatar
                        user={{
                            name: user?.preferredName ?? actorName ?? "Unknown",
                            avatar: user?.avatar ?? "",
                            iconColor: user?.iconColor ?? DEFAULT_ICON_COLOR,
                            email: user?.email ?? null,
                        }}
                        size={size}
                        aria-hidden
                    />
                );
            case ActorKind.RULE:
                return (
                    <RulesIcon
                        className={styles.glyphIcon}
                        data-size={size}
                        aria-hidden
                    />
                );
            case ActorKind.KICK:
            case ActorKind.AGENT:
                return <KickAvatar size={size} aria-hidden />;
            case ActorKind.ORGANIZATION:
                return (
                    <CliIcon
                        className={styles.glyphIcon}
                        data-size={size}
                        aria-hidden
                    />
                );
        }
    },
);

ActorIcon.displayName = "ActorIcon";
